import React, { useState } from "react"
import { CircularProgress, Grid } from "@material-ui/core"
import { Button, Portlet, Text, TextField } from "letalkui"

import Breadcrumb from "@/components/BreadcrumbNew"
import { Loading, Notification } from "@/components"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import ErrorHandlerService from "@/services/ErrorHandler"
import ApiService from "@/services/Api"

import EventCard from "@/pages/Admin/Automation/Webhook/Configure/components/EventCard"
import PayloadExampleDialog from "@/pages/Admin/Automation/Webhook/Configure/components/PayloadExampleDialog"

import useStyles from "@/pages/Admin/Automation/Webhook/Configure/styles"

type WebhookEventType = "message-received" | "message-status" | "message-sent"

type OutboundWebhook = {
	id: number
	instanceId: number
	url: string | null
	events: WebhookEventType[]
	createdAt: string
	updatedAt: string
}

type OutboundWebhookResponse = {
	outboundWebhook: OutboundWebhook
}

type OutboundWebhookState = {
	id: number | null
	url: string
	events: WebhookEventType[]
}

type EventOption = {
	type: WebhookEventType
	title: string
	description: string
	payloadExample: Record<string, unknown>
}

type PayloadDialogContent = {
	title: string
	exampleJson: string
}

const MESSAGE_TYPE_OPTIONS = "text | audio | document | image | video"
const MESSAGE_STATUS_OPTIONS = "sent | delivered | read | failed"
const ORIGIN_OPTIONS = "attendant | bot | ai-agent | message-blast | integration"

const EVENT_OPTIONS: EventOption[] = [
	{
		type: "message-received",
		title: "Mensagem recebida",
		description: "Disparado sempre que a Letalk recebe uma nova mensagem enviada pelo contato",
		payloadExample: {
			event: "message-received",
			instance_id: 8469,
			data: {
				sender: {
					name: "Ana Lima",
					phone: "5541999999999"
				},
				message: {
					id: "638131e92507ad0013c16a7c",
					type: MESSAGE_TYPE_OPTIONS,
					body: "Olá, quero saber sobre o plano",
					caption: "Legenda da imagem (presente apenas em mensagens de imagem com legenda)",
					received_at: "2026-08-12T14:27:03Z"
				}
			}
		}
	},
	{
		type: "message-status",
		title: "Mudança no status da mensagem",
		description: "Disparado quando o status de entrega de uma mensagem enviada é atualizado",
		payloadExample: {
			event: "message-status",
			instance_id: 8469,
			data: {
				message: {
					id: "638131e92507ad0013c16a7c",
					status: MESSAGE_STATUS_OPTIONS,
					changed_at: "2026-08-12T14:27:09Z"
				}
			}
		}
	},
	{
		type: "message-sent",
		title: "Mensagem enviada",
		description: "Disparado quando uma mensagem é enviada ao contato pelo atendente ou bot",
		payloadExample: {
			event: "message-sent",
			instance_id: 8469,
			data: {
				destination: {
					name: "Ana Lima",
					phone: "5541999999999"
				},
				message: {
					id: "638131e92507ad0013c16a7c",
					type: MESSAGE_TYPE_OPTIONS,
					body: "Olá! Como posso ajudar?",
					caption: "Legenda da imagem (presente apenas em mensagens de imagem com legenda)",
					sent_at: "2026-08-12T14:27:12Z"
				},
				origin: ORIGIN_OPTIONS
			}
		}
	}
]

const WEBHOOK_URL_PLACEHOLDER = "https://sua-api.com/letalk/inbox"

const ConfigureWebhook: React.FC = () => {
	const [webhook, setWebhook] = useState<OutboundWebhookState>({ id: null, url: "", events: [] })
	const [isChange, setIsChange] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [isSaving, setIsSaving] = useState(false)
	const [payloadDialogContent, setPayloadDialogContent] = useState<PayloadDialogContent | null>(null)

	const { validation, triggerValidation, clearValidation } = useValidation()
	const classes = useStyles()

	const handleLoadWebhook = async () => {
		try {
			const response = await ApiService.get<OutboundWebhookResponse>("/outbound-webhook")
			const webhookData = response.data.outboundWebhook

			setWebhook({
				id: webhookData.id,
				url: webhookData.url ?? "",
				events: webhookData.events ?? []
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		} finally {
			setIsLoading(false)
		}
	}

	const handleUrlChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		setWebhook((previousWebhook) => ({ ...previousWebhook, url: event.target.value }))
		setIsChange(true)
		clearValidation("url")
	}

	const handleEventToggle = (eventType: WebhookEventType) => {
		setWebhook((previousWebhook) => {
			const isAlreadySelected = previousWebhook.events.includes(eventType)
			const nextEvents = isAlreadySelected
				? previousWebhook.events.filter((selectedEvent) => selectedEvent !== eventType)
				: [...previousWebhook.events, eventType]

			return { ...previousWebhook, events: nextEvents }
		})
		setIsChange(true)
	}

	const handleOpenPayloadDialog = (eventOption: EventOption) => {
		setPayloadDialogContent({
			title: eventOption.title,
			exampleJson: JSON.stringify(eventOption.payloadExample, null, 2)
		})
	}

	const handleClosePayloadDialog = () => {
		setPayloadDialogContent(null)
	}

	const handleSave = async () => {
		setIsSaving(true)
		try {
			await ApiService.put(`/outbound-webhook/${webhook.id}`, {
				url: webhook.url,
				events: webhook.events
			})

			setIsChange(false)
			Notification.success({ message: "Webhook salvo com sucesso!" })
		} catch (error) {
			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		} finally {
			setIsSaving(false)
		}
	}

	useDidMount(() => {
		handleLoadWebhook()
	})

	return (
		<>
			<Grid container direction="column" spacing={4}>
				<Grid item>
					<Breadcrumb
						data={[
							{ name: "Automações", pathname: "/admin/automation" },
							{ name: "Webhook", pathname: "/admin/automation/webhook" },
							{ name: "Configurar", pathname: "/admin/automation/webhook" }
						]}
					/>
				</Grid>

				<Grid item>
					<Text.Title type="h2">
						Configurar webhook
					</Text.Title>
				</Grid>

				<Grid item>
					<Portlet className={classes.container}>
						<Loading
							loading={isLoading}
							customLoadingElement={
								<Grid container justifyContent="center" alignItems="center" className={classes.loadingContainer}>
									<CircularProgress color="secondary" />
								</Grid>
							}
						>
							<Grid container direction="column" spacing={4}>
								<Grid item>
									<TextField
										label="URL do webhook"
										type="text"
										value={webhook.url}
										onChange={handleUrlChange}
										placeholder={WEBHOOK_URL_PLACEHOLDER}
										fullWidth
										errorText={validation.url}
										disabled={isSaving}
									/>
								</Grid>

								<Grid item>
									<Grid container direction="column" spacing={2}>
										<Grid item>
											<Text.Body type="default" sx={{ fontWeight: 600 }}>
												Quais eventos enviar?
											</Text.Body>
										</Grid>
										{EVENT_OPTIONS.map((eventOption) => (
											<EventCard
												key={eventOption.type}
												title={eventOption.title}
												description={eventOption.description}
												isSelected={webhook.events.includes(eventOption.type)}
												onToggle={() => handleEventToggle(eventOption.type)}
												onViewPayloadExample={() => handleOpenPayloadDialog(eventOption)}
											/>
										))}
									</Grid>
								</Grid>

								<Grid item>
									<Grid container justifyContent="flex-end">
										<Grid item>
											<Button.Primary
												size="small"
												onClick={handleSave}
												disabled={isSaving || !isChange}
												endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
											>
												SALVAR
											</Button.Primary>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Loading>
					</Portlet>
				</Grid>
			</Grid>

			{payloadDialogContent && (
				<PayloadExampleDialog
					open
					title={payloadDialogContent.title}
					exampleJson={payloadDialogContent.exampleJson}
					onClose={handleClosePayloadDialog}
				/>
			)}
		</>
	)
}

export default ConfigureWebhook
